import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

/* ============================================================
   ErrorBoundary — Phase 7 polish.

   Route-level boundary wrapped around <AppShellRoutes>, the print
   route and the theme-smoke route in App.tsx. On render error,
   shows a branded fallback card with the error message and a
   "Reload" button. Default behaviour on the happy path is fully
   transparent (passthrough children).
   ============================================================ */

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback override; default renders branded Card. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console for debugging; production may swap to a remote logger.
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <main
        role="alert"
        style={{
          padding: 32,
          maxWidth: 720,
          margin: "48px auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Card style={{ padding: 24 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
            Something went wrong
          </h1>
          <p
            style={{
              marginTop: 12,
              fontSize: 14,
              color: "var(--fg-2, #555)",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              wordBreak: "break-word",
            }}
          >
            {error.message || "Unexpected error"}
          </p>
          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <Button
              type="button"
              onClick={() => {
                this.reset();
                window.location.reload();
              }}
            >
              Reload
            </Button>
            <Button type="button" variant="outline" onClick={this.reset}>
              Try again
            </Button>
          </div>
        </Card>
      </main>
    );
  }
}

export default ErrorBoundary;
